import { type InputHTMLAttributes, forwardRef, useId } from "react";
import clsx from "clsx";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Error message. Accepts `undefined` so callers can pass `errors.field?.message` directly. */
  error?: string | undefined;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, className, id: idProp, ...rest },
  ref,
) {
  const genId = useId();
  const id = idProp ?? (label ? genId : undefined);
  const errId = id ? `${id}-error` : undefined;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label
          htmlFor={id}
          className="text-xs font-medium uppercase tracking-wide text-muted"
        >
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={id}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? errId : undefined}
        className={clsx(
          "w-full rounded-md border bg-surface px-3 py-1.5 text-sm text-fg",
          "placeholder:text-muted/50 transition-colors",
          "focus:outline-none focus:ring-2",
          error
            ? "border-danger focus:ring-[var(--color-danger-muted)]"
            : "border-border focus:border-accent focus:ring-[var(--color-accent-muted)]",
          "disabled:cursor-not-allowed disabled:opacity-40",
          className,
        )}
        {...rest}
      />
      {error && (
        <p id={errId} className="text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
